import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import { isArray } from 'lodash';
import actions from './actions';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { extractDateFromDateTime } from '../../../../helpers/dateTime';
import { PENALTY_RATES_TABS } from './sections/penaltyRatesSection/penaltyRatesSection';
import { t } from '../../../../i18n';

const sectorsApi = new Api('sectors/:id');
const sectorFormDataApi = new Api('sectors/form_data');
const sectorsPositionsApi = new Api('sectors/:id/positions');
const sectorsPenaltyApi = new Api('sectors/:id/penalty_rates');
const positionsApi = new Api('positions/list?pageSize=9999');
const overtimeRatesApi = new Api('sectors/:id/overtime_rates');
const removePositionWarningApi = new Api('sectors/:id/positions/:position_id/remove_warning');
const search = { page: 1, pageSize: 99999 };

export function formatMultiplier(val) {
  let multiplier = val;

  if (typeof val === 'string' && multiplier.includes('.')) {
    multiplier = `${multiplier.replace('.', '')}${val.length === 3 ? '0' : ''}`;
  } else {
    multiplier = +multiplier;

    if (multiplier) {
      if (multiplier >= 100) {
        multiplier = `${multiplier}`.split('');
        multiplier.splice(-2, 0, '.');
        multiplier = multiplier.join('');
      } else {
        multiplier = `0.${multiplier}`;
      }
    }
  }

  return multiplier;
}

function getFormattedPenaltyRates(penaltyRates, startOn) {
  return penaltyRates?.map(item => {
    let multiplier = item.multiplier && item.multiplier.value ? item.multiplier.value : null;
    multiplier = formatMultiplier(multiplier);

    return {
      // eslint-disable-next-line no-underscore-dangle
      _destroy: item._destroy && item._destroy.value,
      id: item.id && item.id.value,
      day: item.day && item.day.value,
      hourly_fee: item.hourly_fee && parseFloat(item.hourly_fee.value),
      multiplier,
      start_time: item.start_time && item.start_time.value,
      end_time: item.end_time && item.end_time.value,
      starts_on: extractDateFromDateTime(startOn),
    };
  });
}

function formatSector(editForm, settings) {
  const { penaltyRatesEnabled, overtimeRatesEnabled } = settings;

  const getIdByPositionId = positionObject => {
    let id = null;
    const position =
      positionObject.position_id &&
      editForm.positions &&
      editForm.positions.find(item => item.position_id && item.position_id.value === positionObject.position_id.value);

    if (position) {
      id = position.id.value;
    } else {
      id = positionObject.id && positionObject.id.value;
    }

    return id;
  };

  const { name, summary, max_hours_per_day, max_hours_per_week, hidden_on_job_boards } = editForm;

  let overtime_rates_attributes;

  if (overtimeRatesEnabled && editForm.overtime_rates_attributes) {
    overtime_rates_attributes = editForm.overtime_rates_attributes.map(item => {
      return {
        // eslint-disable-next-line no-underscore-dangle
        _destroy: item._destroy && item._destroy.value,
        id: item.id && item.id.value,
        week_day: item.week_day && item.week_day.value,
        min_overtime_hours: item.min_overtime_hours.value,
        max_overtime_hours: item.max_overtime_hours.value,
        multiplier: item.multiplier.value,
      };
    });
  }

  const sector_positions_attributes = editForm.position_ids?.map(item => {
    if (item.position_id) {
      return {
        position_id: item.position_id && item.position_id.value,
        ...(getIdByPositionId(item) && { id: getIdByPositionId(item) }),
        // eslint-disable-next-line no-underscore-dangle
        _destroy: item._destroy && item._destroy.value ? true : 0,
      };
    }

    return null;
  });

  const { current_penalty_rates_group, future_penalty_rates_group, current_start_date, future_start_date } = editForm;

  const currentPentaltyRates = isArray(current_penalty_rates_group)
    ? getFormattedPenaltyRates(current_penalty_rates_group, current_start_date.value)
    : [];

  const futurePentaltyRates = isArray(future_penalty_rates_group)
    ? getFormattedPenaltyRates(future_penalty_rates_group, future_start_date.value)
    : [];

  return {
    name: name.value,
    summary: summary.value,
    hidden_on_job_boards: hidden_on_job_boards.value,
    max_hours_per_day: max_hours_per_day.value,
    max_hours_per_week: max_hours_per_week.value,
    sector_positions_attributes,
    overtime_rates_attributes,
    penalty_rates: penaltyRatesEnabled ? [...currentPentaltyRates, ...futurePentaltyRates] : [],
  };
}

function getDataWithErrors(editForm, result) {
  let step = 1;
  ['name', 'summary'].forEach(field => {
    editForm[field].error = result[field] ? result[field][0] : '';
  });

  if (result.overtime_rates) {
    editForm.overtime_rates_attributes.map((item, index) => {
      if (result.overtime_rates[index]) {
        const errorData = result.overtime_rates[index];
        Object.keys(errorData).forEach(key => {
          const [error] = errorData[key];

          if (item[key]) {
            item[key].touched = true;
            item[key].error = error;
          } else if (key === 'base') {
            item.day.touched = true;
            item.day.error = error;
          }
        });
        step = 4;
      }

      return item;
    });
  }

  return { editForm, step };
}

function getDataWithPenaltyRatesErrors(editForm, result) {
  let baseErrors = [];
  let indexBasedOnTab = 0;
  let isAnyError = false;

  Object.values(PENALTY_RATES_TABS).forEach(tab => {
    const penaltyRates = editForm[`${tab}_penalty_rates_group`];

    if (isArray(penaltyRates)) {
      penaltyRates.map(rate => {
        if (result?.penalty_rates[indexBasedOnTab]) {
          if (!rate.day) {
            rate.day = {
              touched: true,
              error: '',
            };
          } else {
            rate.day.touched = true;
          }
          const errors = [];

          Object.keys(result.penalty_rates[indexBasedOnTab]).forEach(key => {
            const errorList = result.penalty_rates[indexBasedOnTab][key];

            if (errorList) {
              errorList.forEach(error => {
                const errorPrefix = key !== 'base' ? t(`dash.admin.sectors.errors.${key}`) : '';
                errors.push(`${errorPrefix} ${error}`);
              });
            }
          });

          rate.day.error = errors;
          isAnyError = true;
        }

        if (result.base) {
          baseErrors = result.base;
          isAnyError = true;
        }

        indexBasedOnTab += 1;

        return rate;
      });
    }
  });

  return { editForm, baseErrors, ...(isAnyError ? { step: 3 } : {}) };
}

function* loadData({ payload }) {
  try {
    const { id, isEdit, settings, tab, addDefaultOne = false } = payload;
    const { penaltyRatesEnabled, overtimeRatesEnabled } = settings;

    let { sector, positions, sectorsPositions, overtimeRates, formInfo } = yield all({
      sector: yield call(sendRequest, sectorsApi.load, { id }, notifications.REQUEST_ERROR),
      formInfo: yield call(sendRequest, sectorFormDataApi.load, {}, notifications.REQUEST_ERROR),
      positions: yield call(sendRequest, positionsApi.load, notifications.REQUEST_ERROR),
      overtimeRates: overtimeRatesEnabled
        ? yield call(sendRequest, overtimeRatesApi.load, { id }, notifications.REQUEST_ERROR)
        : undefined,
      sectorsPositions: yield call(sendRequest, sectorsPositionsApi.load, { id, search }, notifications.REQUEST_ERROR),
    });

    if (sector && formInfo && positions && sectorsPositions) {
      sector = sector.data.success.data;
      sectorsPositions = sectorsPositions.data.success.data;

      positions = positions.data.success.data.records;

      const { totalCount, filteredCount, records } = sectorsPositions;
      sector.positions = records;

      formInfo = {
        ...formInfo.data.success.data,
        positions,
        totalCount,
        filteredCount,
      };

      overtimeRates = overtimeRates?.data?.success?.data || {};
      overtimeRates.records?.forEach(item => {
        item.week_day = formInfo.week_days.find(weekDay => weekDay.id === item.week_day)?.name || '';
      });
      sector.overtime_rates_attributes = overtimeRates.records || [];
      sector.current_start_date = sector.current_penalty_rates_group?.starts_on;
      sector.future_start_date = sector.future_penalty_rates_group?.starts_on;

      /* BE will throw an error if we will send a request for not existing group */
      sector.contains_current_group = !!sector.current_penalty_rates_group;
      sector.contains_future_group = !!sector.future_penalty_rates_group;

      const selectedTab = tab || PENALTY_RATES_TABS.CURRENT;

      let penaltyRates;

      if (penaltyRatesEnabled && sector[`contains_${selectedTab}_group`]) {
        penaltyRates = yield call(
          sendRequest,
          sectorsPenaltyApi.load,
          { id, search: { tab: selectedTab } },
          notifications.REQUEST_ERROR,
        );
      }

      yield put(
        actions.loadDataSuccess({
          data: {
            sector,
            formInfo,
            id,
            ...overtimeRates.maximum_working_hours,
          },
        }),
      );

      penaltyRates = penaltyRates?.data?.success?.data?.records;

      if (penaltyRates) {
        yield put(
          actions.loadSectorPenaltyRatesSuccess({
            data: {
              penaltyRates,
              tab: tab || PENALTY_RATES_TABS.CURRENT,
              sector,
              addDefaultOne,
            },
          }),
        );
      } else if (addDefaultOne) {
        yield put(actions.prepareDefaultPenaltyRate());
      }

      if (isEdit) {
        yield put(actions.edit({ data: { sector, formInfo, id } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadSectorPenaltyRates({ payload }) {
  try {
    const { sector, id, tab, addDefaultOne = false } = payload;
    let penaltyRates = yield call(
      sendRequest,
      sectorsPenaltyApi.load,
      { id, search: { tab: tab || PENALTY_RATES_TABS.CURRENT } },
      notifications.REQUEST_ERROR,
    );

    penaltyRates = penaltyRates?.data?.success?.data?.records;

    if (sector && penaltyRates) {
      yield put(
        actions.loadSectorPenaltyRatesSuccess({
          data: {
            penaltyRates,
            tab,
            sector,
            addDefaultOne,
          },
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadSectorPositions({ payload }) {
  try {
    const { sector, formInfo } = payload;
    const sectorsPositions = yield yield call(
      sendRequest,
      sectorsPositionsApi.load,
      {
        id: sector.id,
        search,
      },
      notifications.REQUEST_ERROR,
    );

    if (sectorsPositions) {
      const { totalCount, filteredCount, records } = sectorsPositions.data.success.data;

      yield put(
        actions.loadDataSuccess({
          data: {
            sector: { ...sector, positions: records },
            formInfo: { ...formInfo, totalCount, filteredCount },
          },
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData() {
  try {
    let { result, formInfo } = yield all({
      result: yield call(sendRequest, positionsApi.load, { search }, notifications.REQUEST_ERROR),
      formInfo: yield call(sendRequest, sectorFormDataApi.load, {}, notifications.REQUEST_ERROR),
    });

    formInfo = formInfo?.data?.success?.data;
    result = result?.data?.success?.data;

    if (result && formInfo) {
      yield put(
        actions.loadDataSuccess({
          data: {
            formInfo: { positions: result.records },
            ...formInfo.maximum_working_hours,
          },
          formInfo,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addSector({ payload }) {
  try {
    const { editForm, settings } = payload;

    const result = yield call(
      sendRequest,
      sectorsApi.createItem,
      { data: { sector: formatSector({ ...editForm }, settings) } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      const { id } = result.data;
      yield put(actions.saveSuccess({}));
      yield put(actions.loadData({ id, settings }));
      yield put(push(`/sectors/${id}`));
    } else {
      yield put(actions.saveFail(getDataWithErrors({ ...editForm }, result)));
      yield put(actions.saveFail(getDataWithPenaltyRatesErrors(editForm, result)));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateSector({ payload }) {
  try {
    const { editForm, id, settings } = payload;

    const result = yield call(
      sendRequest,
      sectorsApi.updateItem,
      {
        data: {
          sector: formatSector(editForm, settings),
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.loadData({ id, settings }));
      yield put(push(`/sectors/${id}`));
    } else {
      yield put(actions.saveFail(getDataWithErrors({ ...editForm }, result)));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateSectorPenaltyRates({ payload }) {
  try {
    const { editForm, id } = payload;

    const { current_penalty_rates_group, future_penalty_rates_group, current_start_date, future_start_date } = editForm;

    const currentPenaltyRates = isArray(current_penalty_rates_group)
      ? getFormattedPenaltyRates(current_penalty_rates_group, current_start_date.value)
      : [];

    const futurePenaltyRates = isArray(future_penalty_rates_group)
      ? getFormattedPenaltyRates(future_penalty_rates_group, future_start_date.value)
      : [];

    const result = yield call(
      sendRequest,
      sectorsPenaltyApi.updateItem,
      {
        data: {
          penalty_rates: [...currentPenaltyRates, ...futurePenaltyRates],
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(push(`/sectors/${id}`));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.sectors.update_sector_penalty_rates_label'),
          }),
        }),
      );
    } else {
      yield put(actions.saveFail(getDataWithPenaltyRatesErrors(editForm, result)));
      yield put(actions.shouldForceChangePenaltyRateTab());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* checkHowManyUnitsInOrganizationRequest({ payload }) {
  const getUnitsAffected = affected_organizations => {
    let unitsAffected = 0;

    if (affected_organizations && affected_organizations.length > 0) {
      affected_organizations.forEach(item => {
        unitsAffected += item.affected_companies_count;
      });
    }

    return unitsAffected;
  };

  try {
    const { position_id, id, positionIndex } = payload;
    let unitsAffected = 0;

    if (position_id) {
      const result = yield call(
        sendRequest,
        removePositionWarningApi.multipleParamsGetRequest,
        { position_id, id },
        notifications.REQUEST_ERROR,
      );

      if (result && result.data && result.data.success && result.data.success.data) {
        const { affected_organizations } = result.data.success.data;
        unitsAffected = getUnitsAffected(affected_organizations);

        if (unitsAffected > 0) {
          yield put(
            actions.checkHowManyUnitsInOrganizationSuccess({ affected_organizations, position_id, positionIndex }),
          );
        } else {
          // Delete position without modal confirmation
          yield put(actions.changeNestedProperty({ attrs: ['position_ids', positionIndex, '_destroy'], value: 1 }));
        }
      }
    } else {
      yield put(actions.changeNestedProperty({ attrs: ['position_ids', positionIndex, '_destroy'], value: 1 }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.LOAD_SECTOR_PENALTY_RATES_REQUEST, loadSectorPenaltyRates),
    takeEvery(actions.UPDATE_SECTOR_PENALTY_RATES_REQUEST, updateSectorPenaltyRates),
    takeEvery(actions.LOAD_SECTOR_POSITIONS, loadSectorPositions),
    takeEvery(actions.ADD, addSector),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.SAVE, updateSector),
    takeEvery(actions.CREATE, loadFormData),
    takeEvery(actions.CHECK_UNITS_IN_ORGANIZATION_REQUEST, checkHowManyUnitsInOrganizationRequest),
  ]);
}
